import React, { useState, useEffect } from 'react'
import Navbar from '../Component/Navbar'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../Api/axiosInstance'
import * as XLSX from 'xlsx'

const statusMap = {
    1: { label: 'Generated',             color: '#e6a817', bg: '#fff8e6', border: '#e6a81740' },  // waiting for driver
    2: { label: 'Accepted',            color: '#4a7c59', bg: '#e8f5ee', border: '#4a7c5940' },  // driver accepted
    3: { label: 'Ongoing',             color: '#1565c0', bg: '#e3f2fd', border: '#1565c040' },  // trip in progress
    4: { label: 'Completed',           color: '#2d6a4f', bg: '#d8f3dc', border: '#2d6a4f40' },  // all stops delivered
    5: { label: 'Cancelled by User',   color: '#c1121f', bg: '#ffe5e5', border: '#c1121f40' },  // cancelled by owner
    6: { label: 'Cancelled by Driver', color: '#7b2d8b', bg: '#f8e8fc', border: '#7b2d8b40' },  // cancelled by driver

}

const navItems = [
    { label: 'Dashboard',        icon: '📊', path: '/dashboard' },
    { label: 'Trip Details',     icon: '🚚', path: '/trip-details' },
    { label: 'Saved Address',    icon: '📍', path: '/save-address' },
    { label: 'Employee Details', icon: '👤', path: '/employee-request' },
    { label: 'Branch Details',   icon: '🏢', path: '/branch-details' },
    { label: 'Account',          icon: '📋', path: '/account' },
]

const OL       = '#6b7c3e'
const OL_LIGHT = '#8a9e52'
const OL_PALE  = '#f0f4e8'
const OL_HEADER = '#e8eedd'
const OL_DARK  = '#3d4a22'

export default function TripDetails() {
    const navigate = useNavigate()

    const [trips, setTrips]               = useState([])
    const [currentPage, setCurrentPage]   = useState(1)
    const [totalPages, setTotalPages]     = useState(1)
    const [totalCount, setTotalCount]     = useState(0)
    const [loading, setLoading]           = useState(false)
    const [exportLoading, setExportLoading] = useState(false)
    const [error, setError]               = useState(null)
    const [activeNav, setActiveNav]       = useState('Trip Details')

    const fetchTrips = async (page) => {
        setLoading(true)
        setError(null)
        try {
            const res = await axiosInstance.post(
                '/enterprise/trip/lists',
                { page, limit: 6 },
                { useAuth: true }
            )
            const data = res.data
            if (data?.data) {
                setTrips(data.data)
                setTotalPages(data.totalPages || 1)
                setTotalCount(data.totalCount || 0)
            } else {
                setTrips([])
            }
        } catch (err) {
            console.error('Failed to fetch trips:', err)
            setError('Failed to load trips. Please try again.')
            setTrips([])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchTrips(currentPage) }, [currentPage])

    const handlePage = (p) => {
        if (p < 1 || p > totalPages) return
        setCurrentPage(p)
    }

    // ── Excel helpers ──────────────────────────────────────────
    const tripsToRows = (tripList) => tripList.map(trip => ({
        'Trip Code':          trip.tripCode,
        'Trip ID':            trip.tripId,
        'Vehicle Type':       trip.estimatedCost?.data?.vehicleType || '—',
        'Distance (km)':      trip.totalDistance,
        'Estimated Cost (₹)': trip.estimatedCost?.data?.discountedPrice || '—',
        'Original Price (₹)': trip.estimatedCost?.data?.originalPrice || '—',
        'Driver Amount (₹)':  trip.driverReceivingAmount,
        'Duration (min)':     trip.estimatedCost?.data?.totalDuration || '—',
        'Status':             statusMap[trip.status]?.label || '—',
        'Enterprise Email':   trip.enterpriseDetails?.email || '—',
        'Enterprise Phone':   trip.enterpriseDetails?.phone || '—',
        'Enterprise Address': trip.enterpriseDetails?.address || '—',
        'Stops Count':        trip.stops?.length || 0,
        'Created At':         new Date(trip.createdAt).toLocaleString(),
    }))

    const downloadExcel = (data, filename) => {
        const ws = XLSX.utils.json_to_sheet(data)
        ws['!cols'] = [
            { wch: 16 }, { wch: 28 }, { wch: 22 }, { wch: 14 },
            { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 14 },
            { wch: 12 }, { wch: 22 }, { wch: 16 }, { wch: 24 },
            { wch: 12 }, { wch: 22 },
        ]
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Trips')
        XLSX.writeFile(wb, filename)
    }

    const handleExportCurrent = () => {
        const rows = tripsToRows(trips)
        downloadExcel(rows, `trips_page_${currentPage}.xlsx`)
    }

    const handleExportAll = async () => {
        setExportLoading(true)
        try {
            const res = await axiosInstance.post(
                '/enterprise/trip/lists',
                { page: 1, limit: totalCount || 1000 },
                { useAuth: true }
            )
            const allTrips = res.data?.data || []
            downloadExcel(
                tripsToRows(allTrips),
                `trips_all_${new Date().toISOString().slice(0, 10)}.xlsx`
            )
        } catch (err) {
            console.error('Export all failed:', err)
        } finally {
            setExportLoading(false)
        }
    }

    return (
        <>
            <Navbar />
            <div style={styles.shell}>

                {/* ── Sidebar ── */}
                <aside style={styles.sidebar}>
                    <div style={styles.sidebarLogo}>
                        <span style={styles.logoIcon}>🚛</span>
                        <span style={styles.logoText}>Menu</span>
                    </div>
                    <nav style={styles.nav}>
                        {navItems.map(item => (
                            <button
                                key={item.label}
                                style={{
                                    ...styles.navItem,
                                    ...(activeNav === item.label ? styles.navItemActive : {})
                                }}
                                onClick={() => {
                                    setActiveNav(item.label)
                                    navigate(item.path)
                                }}
                            >
                                <span style={styles.navIcon}>{item.icon}</span>
                                <span>{item.label}</span>
                                {activeNav === item.label && <span style={styles.navIndicator} />}
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* ── Main Content ── */}
                <main style={styles.main}>

                    {/* Header */}
                    <div style={styles.header}>
                        <div>
                            <h1 style={styles.title}>Trip Details</h1>
                            <p style={styles.subtitle}>Manage and track all enterprise trips</p>
                        </div>
                        <div style={styles.headerRight}>
                            <div style={styles.badge}>
                                <span style={styles.badgeDot} />
                                {totalCount} Total Trips
                            </div>
                            <button
                                style={{
                                    ...styles.exportBtn,
                                    opacity: trips.length === 0 ? 0.5 : 1,
                                    cursor: trips.length === 0 ? 'not-allowed' : 'pointer',
                                }}
                                onClick={handleExportCurrent}
                                disabled={trips.length === 0}
                            >
                                📥 Export Current
                            </button>
                            <button
                                style={{
                                    ...styles.exportBtn,
                                    background: OL,
                                    color: '#fff',
                                    borderColor: OL,
                                    opacity: (exportLoading || totalCount === 0) ? 0.6 : 1,
                                    cursor: (exportLoading || totalCount === 0) ? 'not-allowed' : 'pointer',
                                }}
                                onClick={handleExportAll}
                                disabled={exportLoading || totalCount === 0}
                            >
                                {exportLoading ? '⏳ Exporting...' : '📦 Export All'}
                            </button>
                        </div>
                    </div>

                    {/* Error Banner */}
                    {error && (
                        <div style={styles.errorBanner}>
                            ⚠️ {error}
                            <button style={styles.retryBtn} onClick={() => fetchTrips(currentPage)}>
                                Retry
                            </button>
                        </div>
                    )}

                    {/* Table */}
                    <div style={styles.tableWrapper}>
                        {loading && (
                            <div style={styles.loadingOverlay}>
                                <div style={styles.spinner} />
                            </div>
                        )}

                        {!loading && trips.length === 0 && !error && (
                            <div style={styles.emptyState}>
                                <span style={{ fontSize: 40 }}>🚚</span>
                                <p>No trips found.</p>
                            </div>
                        )}

                        {trips.length > 0 && (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        {['Trip Code', 'Vehicle', 'Distance (km)', 'Estimated Cost (₹)', 'Enterprise Phone', 'Status', 'Action'].map(h => (
                                            <th key={h} style={styles.th}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {trips.map((trip, i) => {
                                        const status  = statusMap[trip.status] || statusMap[1]
                                        const cost    = trip.estimatedCost?.data?.discountedPrice || '—'
                                        const vehicle = trip.estimatedCost?.data?.vehicleType || '—'
                                        return (
                                            <tr
                                                key={trip.tripId}
                                                style={{ ...styles.tr, animationDelay: `${i * 60}ms` }}
                                                className="trip-row"
                                            >
                                                <td style={styles.td}>
                                                    <span style={styles.tripCode}>{trip.tripCode}</span>
                                                </td>
                                                <td style={styles.td}>
                                                    <div style={styles.vehicleCell}>
                                                        <span style={styles.vehicleIcon}>🛵</span>
                                                        <span style={styles.vehicleText}>{vehicle}</span>
                                                    </div>
                                                </td>
                                                <td style={styles.td}>
                                                    <span style={styles.distance}>{trip.totalDistance}</span>
                                                    <span style={styles.distanceUnit}> km</span>
                                                </td>
                                                <td style={styles.td}>
                                                    <span style={styles.cost}>₹{cost}</span>
                                                </td>
                                                <td style={styles.td}>
                                                    <span style={styles.phone}>{trip.enterpriseDetails?.phone || '—'}</span>
                                                </td>
                                                <td style={styles.td}>
                                                    <span style={{
                                                        ...styles.statusBadge,
                                                        color: status.color,
                                                        background: status.bg,
                                                        border: `1px solid ${status.color}40`
                                                    }}>
                                                        {status.label}
                                                    </span>
                                                </td>
                                                <td style={styles.td}>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <button
                                                            style={styles.viewBtn}
                                                            onClick={() => navigate(`/trip/${trip.tripId}`)}
                                                        >
                                                            <span>👁</span> View
                                                        </button>
                                                        <button
                                                            style={styles.viewBtn}
                                                            onClick={() => navigate(`/track/${trip.tripId}`)}
                                                        >
                                                            <span>📍</span> Track
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div style={styles.pagination}>
                            <button
                                style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}
                                onClick={() => handlePage(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                ← Prev
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    style={{ ...styles.pageBtn, ...(p === currentPage ? styles.pageBtnActive : {}) }}
                                    onClick={() => handlePage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}
                                onClick={() => handlePage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next →
                            </button>
                        </div>
                    )}
                </main>

                <style>{`
                    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap');
                    * { box-sizing: border-box; margin: 0; padding: 0; }
                    body { font-family: 'DM Sans', sans-serif; }
                    .trip-row { animation: fadeSlideIn 0.35s ease both; }
                    .trip-row:hover td { background: #f4f7f0 !important; }
                    @keyframes fadeSlideIn {
                        from { opacity: 0; transform: translateY(8px); }
                        to   { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes spin { to { transform: rotate(360deg); } }
                `}</style>
            </div>
        </>
    )
}

const styles = {
    shell: {
        display: 'flex',
        minHeight: '100vh',
        fontFamily: "'DM Sans', sans-serif",
        background: '#f7f8f4',
        color: '#2c2c2c',
    },
    sidebar: {
        width: 220,
        background: '#fff',
        borderRight: '1px solid #e0e5d4',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 0',
        flexShrink: 0,
        boxShadow: '2px 0 12px rgba(107,124,62,0.07)',
    },
    sidebarLogo: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 20px 24px',
        borderBottom: '1px solid #e8eedd',
        marginBottom: 12,
    },
    logoIcon: { fontSize: 22 },
    logoText: {
        fontSize: 18,
        fontWeight: 700,
        color: OL_DARK,
        letterSpacing: '-0.3px',
    },
    nav: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '0 10px',
    },
    navItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 10,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 500,
        color: '#666',
        textAlign: 'left',
        position: 'relative',
        transition: 'all 0.18s ease',
        fontFamily: "'DM Sans', sans-serif",
    },
    navItemActive: {
        background: OL_PALE,
        color: OL_DARK,
        fontWeight: 600,
    },
    navIcon: { fontSize: 15 },
    navIndicator: {
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 3,
        height: 20,
        borderRadius: '0 3px 3px 0',
        background: OL,
    },
    main: {
        flex: 1,
        padding: '32px 36px',
        maxWidth: 'calc(100vw - 220px)',
        overflow: 'auto',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 28,
        gap: 16,
        flexWrap: 'wrap',
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
    },
    title: {
        fontSize: 26,
        fontWeight: 700,
        color: OL_DARK,
        letterSpacing: '-0.5px',
    },
    subtitle: {
        fontSize: 13,
        color: '#888',
        marginTop: 3,
    },
    badge: {
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        background: OL_PALE,
        border: `1px solid ${OL}30`,
        color: OL_DARK,
        borderRadius: 20,
        padding: '6px 14px',
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    badgeDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: OL,
        display: 'inline-block',
    },
    exportBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 16px',
        borderRadius: 9,
        border: `1.5px solid ${OL}`,
        background: '#fff',
        color: OL_DARK,
        fontWeight: 600,
        fontSize: 13,
        cursor: 'pointer',
        fontFamily: "'DM Sans', sans-serif",
        transition: 'all 0.15s ease',
        whiteSpace: 'nowrap',
    },
    errorBanner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#fee2e2',
        border: '1px solid #fca5a5',
        color: '#b91c1c',
        borderRadius: 10,
        padding: '12px 16px',
        marginBottom: 16,
        fontSize: 14,
        fontWeight: 500,
    },
    retryBtn: {
        padding: '5px 14px',
        borderRadius: 7,
        border: '1.5px solid #b91c1c',
        background: '#fff',
        color: '#b91c1c',
        fontWeight: 600,
        fontSize: 13,
        cursor: 'pointer',
    },
    tableWrapper: {
        background: '#fff',
        borderRadius: 14,
        border: '1px solid #e2e8d4',
        overflow: 'hidden',
        boxShadow: '0 2px 16px rgba(107,124,62,0.07)',
        position: 'relative',
        minHeight: 120,
    },
    loadingOverlay: {
        position: 'absolute',
        inset: 0,
        background: 'rgba(255,255,255,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
    },
    spinner: {
        width: 32,
        height: 32,
        border: `3px solid ${OL_PALE}`,
        borderTop: `3px solid ${OL}`,
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '48px 0',
        color: '#aaa',
        fontSize: 15,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 14,
    },
    th: {
        background: OL_HEADER,
        color: OL_DARK,
        fontWeight: 600,
        padding: '13px 16px',
        textAlign: 'left',
        fontSize: 13,
        letterSpacing: '0.01em',
        borderBottom: `2px solid ${OL}30`,
        whiteSpace: 'nowrap',
    },
    tr: {
        borderBottom: '1px solid #eef1e8',
        transition: 'background 0.15s',
    },
    td: {
        padding: '13px 16px',
        color: '#3a3a3a',
        verticalAlign: 'middle',
        background: '#fff',
        transition: 'background 0.15s',
    },
    tripCode: {
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 13,
        fontWeight: 500,
        color: OL_DARK,
        background: OL_PALE,
        padding: '3px 8px',
        borderRadius: 6,
    },
    vehicleCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 7,
    },
    vehicleIcon: { fontSize: 17 },
    vehicleText: { color: '#555', fontSize: 13 },
    distance: { fontWeight: 600, color: OL_DARK },
    distanceUnit: { color: '#999', fontSize: 12 },
    cost: { fontWeight: 700, color: OL, fontSize: 15 },
    phone: { color: '#555', letterSpacing: '0.02em' },
    statusBadge: {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
    },
    viewBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '6px 12px',
        borderRadius: 8,
        border: `1.5px solid ${OL}60`,
        background: '#fff',
        color: OL_DARK,
        fontWeight: 600,
        fontSize: 12,
        cursor: 'pointer',
        fontFamily: "'DM Sans', sans-serif",
        transition: 'all 0.15s ease',
        whiteSpace: 'nowrap',
    },
    pagination: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 6,
        marginTop: 20,
    },
    pageBtn: {
        padding: '7px 14px',
        borderRadius: 8,
        border: '1.5px solid #dde5cc',
        background: '#fff',
        color: '#555',
        fontWeight: 500,
        fontSize: 13,
        cursor: 'pointer',
        fontFamily: "'DM Sans', sans-serif",
        transition: 'all 0.15s ease',
    },
    pageBtnActive: {
        background: OL,
        color: '#fff',
        border: `1.5px solid ${OL}`,
        fontWeight: 700,
    },
    pageBtnDisabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
    },
}